import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { PiggyBank, ArrowRight, Minus, Plus, MoveVertical } from "lucide-react";
import api from "@/lib/api";
import { inr } from "@/lib/constants";

const ACTION_ICON = { remove: Minus, add: Plus, adjust: MoveVertical };

export default function CheaperTwinCard({ formulaId }) {
  const { data, isLoading } = useQuery({
    queryKey: ["cheaper-twin", formulaId],
    queryFn: async () => (await api.get(`/formulas/${formulaId}/cheaper-twin`)).data,
  });

  return (
    <div className="bg-white border border-[#E8DFD1] rounded-xl shadow-sm overflow-hidden" data-testid="cheaper-twin-card">
      <div className="flex items-center gap-2 px-6 py-4 border-b border-[#F4EFEA]">
        <PiggyBank size={17} className="text-[#C5A059]" />
        <h2 className="font-display text-lg font-semibold text-[#1C100B]">Cheaper Twin</h2>
      </div>

      {isLoading || !data ? (
        <div className="px-6 py-6 text-sm text-[#8C7A6B]" data-testid="cheaper-twin-loading">Scanning the library…</div>
      ) : !data.twin ? (
        <div className="px-6 py-6 text-sm text-[#8C7A6B]" data-testid="cheaper-twin-empty">{data.reason}</div>
      ) : (
        <div className="px-6 py-5 space-y-5" data-testid="cheaper-twin-result">
          <div className="flex flex-wrap items-center gap-3">
            <div className="text-sm text-[#5C4A3E]">
              Closest match on taste:{" "}
              <Link to={`/formulas/${data.twin.id}`} className="font-semibold text-[#B08B46] hover:underline" data-testid="cheaper-twin-link">
                {data.twin.name}
              </Link>
            </div>
            <span className="text-[10px] font-bold uppercase tracking-wider bg-[#F0FDF4] text-[#15803D] border border-[#BBF7D0] px-2 py-1 rounded-full" data-testid="cheaper-twin-saving">
              saves {inr(data.twin.saving_per_kg)}/kg
            </span>
            <span className="text-xs text-[#8C7A6B]" data-testid="cheaper-twin-distance">
              sensory distance {data.twin.sensory_distance} (≤ {data.threshold} counts as a twin)
            </span>
          </div>

          <div className="grid grid-cols-2 gap-4 text-sm">
            <div className="border border-[#F4EFEA] rounded-lg px-4 py-3">
              <div className="text-[10px] uppercase tracking-wider text-[#8C7A6B] font-semibold">This formula</div>
              <div className="font-bold text-[#2A1810]">{inr(data.base.bom_cost_per_kg)}/kg</div>
            </div>
            <div className="border border-[#F4EFEA] rounded-lg px-4 py-3 bg-[#FAF6F0]">
              <div className="text-[10px] uppercase tracking-wider text-[#8C7A6B] font-semibold">{data.twin.name}</div>
              <div className="font-bold text-[#15803D]">{inr(data.twin.bom_cost_per_kg)}/kg</div>
            </div>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-wider text-[#8C7A6B] font-semibold mb-2">
              Swaps that get you there
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#FAF6F0] text-[#2A1810] text-xs uppercase tracking-wider">
                  <tr>
                    <th className="px-4 py-2.5 text-left font-semibold">Ingredient</th>
                    <th className="px-4 py-2.5 text-right font-semibold">Now</th>
                    <th className="px-4 py-2.5 text-right font-semibold">Target</th>
                    <th className="px-4 py-2.5 text-right font-semibold">Cost impact</th>
                  </tr>
                </thead>
                <tbody>
                  {data.swaps.map((s, i) => {
                    const Icon = ACTION_ICON[s.action] || MoveVertical;
                    return (
                      <tr key={i} className="border-b border-[#F4EFEA] last:border-0" data-testid={`cheaper-twin-swap-${i}`}>
                        <td className="px-4 py-2.5 font-medium text-[#2A1810]">
                          <Icon size={12} className="inline mr-1.5 -mt-0.5 text-[#8C7A6B]" />
                          {s.ingredient_name}
                        </td>
                        <td className="px-4 py-2.5 text-right text-[#5C4A3E]">
                          {s.from_percentage == null ? <span className="text-[#8C7A6B]">absent</span> : `${s.from_percentage}%`}
                        </td>
                        <td className="px-4 py-2.5 text-right text-[#5C4A3E]">
                          {s.to_percentage == null ? <span className="text-[#8C7A6B]">drop</span> : `${s.to_percentage}%`}
                        </td>
                        <td className={`px-4 py-2.5 text-right font-semibold ${s.cost_delta <= 0 ? "text-[#15803D]" : "text-[#B91C1C]"}`}>
                          {s.cost_delta > 0 ? "+" : ""}{inr(s.cost_delta)}
                        </td>
                      </tr>
                    );
                  })}
                  {!data.swaps.length && (
                    <tr><td colSpan={4} className="px-4 py-6 text-center text-[#8C7A6B]">Both formulas share the same lines.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {data.candidates.length > 1 && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-[#8C7A6B] font-semibold mb-2">
                Other close profiles
              </div>
              <div className="space-y-2">
                {data.candidates.filter((c) => c.id !== data.twin.id).map((c) => (
                  <div key={c.id} className="flex items-center justify-between gap-3 text-sm" data-testid={`cheaper-twin-candidate-${c.id}`}>
                    <Link to={`/formulas/${c.id}`} className="text-[#2A1810] hover:text-[#B08B46] truncate">{c.name}</Link>
                    <span className="text-xs text-[#8C7A6B] shrink-0">
                      {inr(c.bom_cost_per_kg)}/kg · distance {c.sensory_distance}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <Link
            to={`/formulas/compare?ids=${data.base.id},${data.twin.id}`}
            className="inline-flex items-center gap-1 text-xs font-semibold text-[#B08B46] hover:underline"
            data-testid="cheaper-twin-compare-link"
          >
            Compare side by side <ArrowRight size={12} />
          </Link>
        </div>
      )}
    </div>
  );
}
